import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable, Subject } from "rxjs";
import { NewProjectForm } from "../components/add-new-project-dialog/add-new-project-dialog.component";
import { ProjectStatus } from "../enum/project-status.enum";
import { environment } from "../environments/environment";
import { FilterObject } from "../interfaces/filter-object.interface";
import { LaborHoursData } from "../interfaces/labor-hours-data.interface";
import {
  PaginatedResponse,
  Pagination,
} from "../interfaces/pagination.interface";
import {
  CreateProjectTableDto,
  PricingTableRowData,
  ProjectTableLocationCategory,
} from "../interfaces/pricing-table.interface";
import { ProjectOverview } from "../interfaces/project-overview.interface";
import { ProjectTimeTrackingData } from "../interfaces/project-time-tracking-data.interface";
import {
  Project,
  ProjectVersion,
  ProjectVersionTable,
  ProjectVersionTableRow,
} from "../interfaces/project.interface";
import { Proposal } from "../interfaces/proposal.interface";
import { ActivityService } from "./activity.service";
import { UtilService } from "./util.service";

@Injectable({
  providedIn: "root",
})
export class ProjectManagementService {
  private _reloadRequired = new Subject<void>();
  readonly CHANGE_ORDER_TABLE_NAME_TITLE_KEY = "changeOrders";

  constructor(
    private http: HttpClient,
    private utilService: UtilService,
    private activityService: ActivityService,
  ) {}

  getProjects(
    pagination: Pagination,
    filterObject?: FilterObject,
    skipHotlistPriority?: boolean,
  ): Observable<PaginatedResponse<Project>> {
    let pagObj: { page: number; size: number; sort?: string[] } = {
      page: pagination.page,
      size: pagination.size,
    };
    if (pagination?.sort || !skipHotlistPriority) {
      const sort = ["isHotlisted,desc"];
      if (pagination.sort) {
        sort.push(pagination.sort);
      }
      pagObj.sort = sort;
    }
    let obj = filterObject ? { ...pagObj, ...filterObject } : pagObj;
    (obj as any).includeVersions = true;
    const params = this.utilService.objectToHttpParams(obj);
    return this.http
      .get<PaginatedResponse<Project>>(`${environment.API_URL}projects`, {
        params,
      })
      .pipe(
        // MANUAL REMOVE OF ONE PROJECT THAT IS AUTOGENERATED BY BACKEND
        map((r) => ({
          ...r,
          content: r.content.filter(
            (p) => p.title !== "UNASSIGNED TIME ENTRIES",
          ),
        })),
      );
  }

  getProjectById(id: string): Observable<Project> {
    return this.http.get<Project>(`${environment.API_URL}projects/${id}`, {
      params: { includeVersions: true },
    });
  }

  editProject(project: Project): Observable<Project> {
    return this.http.put<Project>(
      `${environment.API_URL}projects/${project.id}`,
      project,
      { params: { includeVersions: true } },
    );
  }

  removeProject(project: Project): Observable<Project> {
    return this.http.delete<Project>(
      `${environment.API_URL}projects/${project.id}`,
    );
  }

  hotlistProject(project: Project, newValue: boolean): Observable<Project> {
    project.isHotlisted = newValue;
    return this.editProject(project);
  }

  addNewProject(project: NewProjectForm): Observable<Project> {
    const projectObj = {
      ...this.buildProjectPatch(project),
      status: ProjectStatus.PRICING,
    };
    return this.http.post<Project>(
      `${environment.API_URL}projects`,
      projectObj,
    );
  }

  editProjectFromForm(
    project: Project,
    form: NewProjectForm,
  ): Observable<Project> {
    const merged = {
      ...project,
      ...this.buildProjectPatch(form),
      projectManagerName: form.projectManager.name,
    } as Project;
    return this.editProject(merged);
  }

  private buildProjectPatch(form: NewProjectForm) {
    let countryString = "";
    if (form.address.state) {
      countryString = `${form.address.state}, ${form.address.country}`;
    } else {
      countryString = `${form.address.country}`;
    }
    return {
      title: form.title,
      address: {
        street: form.address.address_line1,
        city: form.address.city,
        zipCode: form.address.postcode,
        country: countryString,
        latitude: form.address.lat,
        longitude: form.address.lon,
      },
      projectManagerId: form.projectManager.id,
      quantitiesTakeOffSheetUrl: form.quantitiesTakeOffSheetUrl,
    };
  }

  getReloadRequiredObservable(): Subject<void> {
    return this._reloadRequired;
  }

  triggerReload(): void {
    this._reloadRequired.next();
  }

  removeCrewMemberFromProject(
    projectId: string,
    crewMemberId: string,
  ): Observable<void> {
    return this.http.delete<void>(
      `${environment.API_URL}projects/${projectId}/members/${crewMemberId}`,
    );
  }

  createNewProjectVersion(
    projectId: string,
    versionInfo: Partial<ProjectVersion>,
  ): Observable<ProjectVersion> {
    return this.http.post<ProjectVersion>(
      `${environment.API_URL}projects/${projectId}/versions`,
      versionInfo,
    );
  }

  deleteVersion(projectId: string, versionId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.API_URL}projects/${projectId}/versions/${versionId}?force=true`,
    );
  }

  editVersion(
    projectId: string,
    versionId: string,
    newVersion: ProjectVersion,
  ): Observable<ProjectVersion> {
    return this.http.put<ProjectVersion>(
      `${environment.API_URL}projects/${projectId}/versions/${versionId}`,
      newVersion,
    );
  }

  duplicateVersion(
    projectId: string,
    versionId: string,
    newName: string,
  ): Observable<ProjectVersion> {
    return this.http.post<ProjectVersion>(
      `${environment.API_URL}projects/${projectId}/versions/${versionId}/clone`,
      { newName },
    );
  }

  bulkCreateTables(
    projectId: string,
    tables: { tables: Partial<CreateProjectTableDto>[] },
  ): Observable<CreateProjectTableDto[]> {
    return this.http.post<CreateProjectTableDto[]>(
      `${environment.API_URL}projects/${projectId}/tables`,
      tables,
    );
  }

  deleteTable(projectId: string, tableId: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.API_URL}projects/${projectId}/tables/${tableId}`,
      { params: { force: true } },
    );
  }

  bulkCreateTableRows(
    projectId: string,
    tableId: string,
    rows: PricingTableRowData[],
  ): Observable<any> {
    return this.http.post<any>(
      `${environment.API_URL}projects/${projectId}/tables/${tableId}/rows/bulk-array`,
      rows,
    );
  }

  bulkUpdateTableRows(
    projectId: string,
    tableId: string,
    rows: { id: string; data: any }[],
  ): Observable<any> {
    return this.http.put<any>(
      `${environment.API_URL}projects/${projectId}/tables/${tableId}/rows/bulk-array`,
      rows,
    );
  }

  getTablesForVersion(
    projectId: string,
    versionId: string,
    includeRows = true,
  ): Observable<ProjectVersionTable[]> {
    return this.http.get<ProjectVersionTable[]>(
      `${environment.API_URL}projects/${projectId}/versions/${versionId}/tables`,
      { params: { includeRows } },
    );
  }

  updateTableRow(
    projectId: string,
    tableId: string,
    rowId: string,
    newData: { data: any },
  ): Observable<void> {
    return this.http.put<void>(
      `${environment.API_URL}projects/${projectId}/tables/${tableId}/rows/${rowId}`,
      newData,
    );
  }

  deleteTableRow(
    projectId: string,
    tableId: string,
    rowId: string,
  ): Observable<void> {
    return this.http.delete<void>(
      `${environment.API_URL}projects/${projectId}/tables/${tableId}/rows/${rowId}`,
    );
  }

  addTableRow(
    projectId: string,
    tableId: string,
    newData: { data: any },
  ): Observable<ProjectVersionTableRow> {
    return this.http.post<ProjectVersionTableRow>(
      `${environment.API_URL}projects/${projectId}/tables/${tableId}/rows`,
      newData,
    );
  }

  updateTable(
    projectId: string,
    tableId: string,
    toUpdate: Partial<ProjectVersionTable>,
  ): Observable<ProjectVersionTable> {
    return this.http.put<ProjectVersionTable>(
      `${environment.API_URL}projects/${projectId}/tables/${tableId}`,
      { ...toUpdate },
    );
  }

  getAllProposals(
    pagination: Pagination,
    filter: { [key: string]: string },
  ): Observable<PaginatedResponse<Proposal>> {
    const params = this.utilService.objectToHttpParams({
      ...pagination,
      ...filter,
    });
    return this.http.get<PaginatedResponse<Proposal>>(
      `${environment.API_URL}proposals`,
      {
        params,
      },
    );
  }

  getProposalByProjectVersionId(versionId: string): Observable<Proposal> {
    return this.http.get<Proposal>(
      `${environment.API_URL}proposals/by-project-version/${versionId}`,
    );
  }

  createProposal(newProp: Partial<Proposal>): Observable<Proposal> {
    return this.http.post<Proposal>(`${environment.API_URL}proposals`, newProp);
  }

  editProposal(editedProp: Proposal): Observable<Proposal> {
    return this.http.put<Proposal>(
      `${environment.API_URL}proposals/${editedProp.id}`,
      editedProp,
    );
  }

  getProjectOverview(
    projectId: string,
    locationCategory: "BUILDING" | "SITE" | "UNCATEGORIZED",
  ): Observable<ProjectOverview> {
    return this.http.get<ProjectOverview>(
      `${environment.API_URL}projects/${projectId}/overview`,
      { params: { locationCategory } },
    );
  }

  getChartData(
    projectId: string,
    locationCategory: ProjectTableLocationCategory,
  ): Observable<any> {
    return this.http.get<any>(
      `${environment.API_URL}projects/${projectId}/chart-data`,
      { params: { locationCategory } },
    );
  }

  projectVersionWin(projectId: string, versionId: string): Observable<Project> {
    return this.http.put<Project>(
      `${environment.API_URL}projects/${projectId}/versions/${versionId}/win`,
      {},
    );
  }

  projectLost(projectId: string): Observable<Project> {
    return this.http.put<Project>(
      `${environment.API_URL}projects/${projectId}/cancel`,
      {},
    );
  }

  getProjectLaborHours(projectId: string): Observable<LaborHoursData> {
    return this.http.get<LaborHoursData>(
      `${environment.API_URL}projects/${projectId}/labor-hours`,
    );
  }

  getProjectTimeTrackingData(
    projectId: string,
    params?: { startDate: string; endDate: string },
  ): Observable<ProjectTimeTrackingData> {
    return this.http.get<ProjectTimeTrackingData>(
      `${environment.API_URL}projects/${projectId}/time-tracker`,
      { params },
    );
  }

  tableAlertChange(
    projectId: string,
    tableId: string,
    alertEnabled: boolean,
  ): Observable<void> {
    return this.http.patch<void>(
      `${environment.API_URL}projects/${projectId}/tables/${tableId}/alert`,
      { alertEnabled },
    );
  }

  archiveProject(project: Project): Observable<Project> {
    return this.http.put<Project>(
      `${environment.API_URL}projects/${project.id}/archive`,
      {},
    );
  }

  unArchiveProject(project: Project): Observable<Project> {
    return this.http.put<Project>(
      `${environment.API_URL}projects/${project.id}/unarchive`,
      {},
    );
  }

  deleteProject(project: Project): Observable<void> {
    return this.http.delete<void>(
      `${environment.API_URL}projects/${project.id}`,
    );
  }
}
